import type { CardAssetRecomendationData } from '.';
import type { Dispatch, FC, SetStateAction } from 'react';
import { MoreVertical } from '@eqi-investimentos/icons/web';
import { Box, Flex, Text } from '@eqi-investimentos/react';
import { Tag, TagLabel } from '@chakra-ui/react';
import { IconButton } from '@/components/elements/IconButton';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender } from '@/utils/jsx';

export const FrontFace: FC<FrontFaceProps> = ({
  data: { name, recommendation, thumb, ticker, subcategory },
  setIsShowingFront,
}) => {
  return (
    <Box
      pos="relative"
      w="13.25rem"
      h="9.375rem"
      p="1rem"
      borderRadius="xs"
      bgColor="background.alwaysDark"
      cursor="pointer"
      onClick={() => setIsShowingFront(() => false)}
    >
      <Flex
        pos="relative"
        justifyContent="flex-end"
        pr="1"
        pl="2.5"
        pt="0.3125rem"
        pb="4"
      >
        <IconButton
          icon={MoreVertical}
          ariaLabel="More options"
          iconProps={{
            w: 20,
            h: 20,
            transform: 'rotate(90deg)',
            color: 'content.alwaysLight',
          }}
        />
      </Flex>
      <Flex flexDir="column" px="4" gap="m">
        <Flex alignItems="center" gap="m">
          <Flex
            alignItems="center"
            justifyContent="center"
            w="3.125rem"
            h="3.125rem"
            bgColor="content.alwaysLight"
            borderRadius="md"
          >
            <Thumb
              src={thumb}
              width={41}
              height={41}
              fit="cover"
              radius={5}
              alt={name}
            />
          </Flex>
          <Flex flexDir="column" gap="0.5">
            <Text
              className="container__cad_recommendation_label_name"
              maxW="7.5rem"
              size="xl"
              fontWeight="normal"
              color="content.inverse"
              noOfLines={1}
            >
              {name}
            </Text>
            <Text
              size="m"
              fontWeight="normal"
              lineHeight="md"
              color="content.brandLight"
            >
              {ticker}
            </Text>
          </Flex>
        </Flex>
        <Flex alignItems="center" gap="xs">
          <IfRender condition={!!subcategory}>
            <Tag
              px="0.3125rem"
              minW="2.375rem"
              minH="5"
              maxH="5"
              color="content.alwaysDark"
              bgColor="content.alwaysLight"
              borderRadius="0.125rem"
            >
              <TagLabel fontSize="s" fontWeight="semibold" lineHeight="5">
                {subcategory}
              </TagLabel>
            </Tag>
          </IfRender>
          <Tag
            px="0.3125rem"
            minW="2.375rem"
            minH="5"
            maxH="5"
            color={mapRecommendationStatus[recommendation].color}
            bgColor={mapRecommendationStatus[recommendation].bg}
            borderRadius="0.125rem"
          >
            <TagLabel fontSize="s" fontWeight="semibold" lineHeight="5">
              {mapRecommendationStatus[recommendation].label}
            </TagLabel>
          </Tag>
        </Flex>
      </Flex>
    </Box>
  );
};

const mapRecommendationStatus = {
  buy: { label: 'Compra', bg: 'alert.success', color: 'content.alwaysLight' },
  sell: { label: 'Venda', bg: 'alert.error', color: 'content.alwaysLight' },
  keep: {
    label: 'Manter',
    bg: 'alert.informationLight',
    color: 'content.alwaysDark',
  },
  neutral: {
    label: 'Neutro',
    bg: 'alert.informationLight',
    color: 'content.alwaysDark',
  },
};

type FrontFaceProps = {
  data: CardAssetRecomendationData;
  setIsShowingFront: Dispatch<SetStateAction<boolean>>;
};
